import styled from "styled-components";
import { consumeSignupDiscountApplied } from "src/Module/Checkout/signupDiscountNotice";
import { checkoutBannerCopy, hasOnlySignupDetails } from "src/Module/Checkout/signupOfferConfig";
import { selectAccount } from "@redux";
import { signupOfferCopy } from "src/Module/Checkout/signupOfferConfig";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { ProcessOrderHeader, ClipLoaderCommon, ConfirmComponent } from "@components";
import {
    useAppSelector,
    AppDispatch,
    selectBookNowRedeemer,
    fetchBookNowRedeemer,
    createNewBookNowRedeemer,
    selectBookNow,
    setBookNowItemData,
    selectCheckout,
    selectAuth,
    loginV2,
    setJwt,
    initializeAccountDetail,
    store,
} from "@redux";
import { fetchBookNowItems } from "@redux";
import { AppModuleLayout } from "@layouts";
import { breakpoint, lsEmail, sessionHelper, ssCheckoutFlow, theme } from "@utilities";
import { lsJwt, ssStorage } from "src/Utilities/storage";
import { LoadingStage } from "src/Interfaces/shared";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { BookNowBreadcrumbs, PATH_BOOKING, PATH_HOME } from "@routes";
import { IAuthLogin, ICartItemData, IPayNowResponseData, ISetCustomerData } from "@interfaces";
import _ from "lodash";

import { authApi, cartApi } from "@api";
import {
    BookNowStepLabels,
    buildBookNowCheckoutContext,
    CheckoutCartStepLabels,
    transformCartItemsToUpdateCartItemsData,
} from "src/Module/Checkout/checkoutHelpers";
import { submitCartDataAndFetch } from "src/Module/Checkout/checkout";
import { CheckoutStep2 } from "src/Module/Checkout/Components/CheckoutStep2";
import { CheckoutStep1 } from "src/Module/Checkout/Components/CheckoutStep1";
import { CheckoutProvider } from "../../Module/Checkout/CheckoutContext";
import { useStepPersistence } from "src/Module/Checkout/CheckoutStepHelper";
import { useNavigate, useSearchParams } from "react-router-dom";
import { NavLink } from "react-router-dom";
import { PaymentRedirectOverlay } from "src/Module/Checkout/Components/PaymentRedirectOverlay";
import { useGoogleOAuth } from "src/Module/Checkout/hooks/useGoogleOAuth";
import { useGuestCheckout } from "src/Module/Checkout/hooks/useGuestCheckout";
import { GuestLoginModal } from "src/Module/Checkout/Components/GuestLoginModal";

export const BookNow = () => {
    //page hook
    const dispatch = useDispatch<AppDispatch>();
    const [isProcessingOAuth, setIsProcessingOAuth] = useState(false);
    // Set by the Book Now offer sheet on sign-up, just before the pending action
    // brings the visitor here. Read once so a later visit does not re-announce it.
    const [showDiscountNotice, setShowDiscountNotice] = useState(() =>
        consumeSignupDiscountApplied(),
    );
    // Handle Google OAuth callback at page level

    //redux state
    const { activeDiscount } = useAppSelector(selectCheckout);
    const { cartItems, cartItemsLoadingStage } = useAppSelector(selectBookNow);
    const bookNowCustomers = useAppSelector(selectBookNowRedeemer);
    const { jwt } = useAppSelector(selectAuth);
    const { account } = useAppSelector(selectAccount);
    // Same rule as the traveller form's "Save and Earn Points": a signed-in
    // visitor who is not yet an agent is the one with a discount still to claim.
    const canEarnPoints = !!jwt && !!account && !account.is_agent_integrated;
    /**
     * Whether the banner prompting the visitor to add their details belongs at the
     * top of the page.
     *
     * `canEarnPoints` alone stopped being enough once sign-up started upgrading
     * the account to a points agent straight away: that clears
     * is_agent_integrated's falsiness before the visitor ever reaches this page,
     * so the banner never appeared for the very people it was written for. They
     * are already earning, but their profile still holds nothing but the three
     * fields sign-up collected, and the banner is what points them at the
     * traveller form to replace the stand-ins. It clears itself once that form
     * writes real details to the profile.
     */
    const shouldPromptForDetails =
        !!jwt && !!account && (canEarnPoints || hasOnlySignupDetails(account));
    const isLoggedIn = useMemo(() => !_.isNil(jwt), [jwt]);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const createGuestAccountPostOrder = async (email: string) => {
        try {
            const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%";
            let randomPassword = "";
            for (let i = 0; i < 16; i++) {
                randomPassword += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            const signUpRes = await authApi.signUp({
                email,
                password: randomPassword,
                is_password_randomly_generated: true,
            });
            if (signUpRes.data.code === 200) {
                const loginRes = (await dispatch(loginV2({ email, password: randomPassword }))) as {
                    payload: IAuthLogin;
                };
                if (loginRes.payload.data?.accessToken) {
                    lsJwt.setItem(loginRes.payload.data);
                    lsEmail.setItem(email);
                    dispatch(setJwt(loginRes.payload.data));
                    await dispatch(initializeAccountDetail()).unwrap();
                    authApi.resendVerificationOtp().catch(console.error);
                }
            }
        } catch (err) {
            console.error("Post-order guest account creation failed:", err);
        }
        sessionStorage.removeItem("guestPrimaryEmail");
    };

    const sessionId = useMemo(() => {
        const urlSessionId = searchParams.get("sessionId");
        if (urlSessionId) {
            return urlSessionId;
        }
        if (!isLoggedIn && sessionHelper.hasBookNowSession()) {
            return sessionHelper.getBookNowSessionId();
        }
        return undefined;
    }, [searchParams, isLoggedIn]);

    useEffect(() => {
        if (!isLoggedIn && sessionId && !searchParams.get("sessionId")) {
            setSearchParams({ sessionId }, { replace: true });
        }
    }, [sessionId, isLoggedIn]);

    useGoogleOAuth({
        isProcessingOAuth,
        setIsProcessingOAuth,
        sessionId,
    });

    const hasBookingData = useMemo(() => {
        return cartItems.some((item) => item.booking_data && item.booking_data.length > 0);
    }, [cartItems]);

    const hasValidItems = useMemo(() => {
        return !cartItems.some((item) => {
            // Check product/availability flags
            if (item.is_product_latest === false || item.is_availability_latest === false) {
                return true;
            }
            // Check if booking date has passed
            const bookingDate = new Date(item.booking_date);
            bookingDate.setHours(0, 0, 0, 0);
            const currentDate = new Date();
            currentDate.setHours(0, 0, 0, 0);
            return bookingDate < currentDate;
        });
    }, [cartItems]);

    const isLoading = cartItemsLoadingStage === LoadingStage.IS_INITIALIZING;

    //page state
    const { activeStepIndex, setActiveStep } = useStepPersistence(
        hasBookingData && hasValidItems,
        isLoading,
        "booknow-step",
        !hasValidItems,
    );
    const [isOrderSubmitting, setIsOrderSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteQueue, setDeleteQueue] = useState<
        { id: string | number; type: "cart" | "group" | "product" | "item" }[]
    >([]);

    const guestCheckout = useGuestCheckout({
        sessionId,
        customers: bookNowCustomers,
        isLoggedIn,
    });

    useEffect(() => {
        if (cartItemsLoadingStage === LoadingStage.INITIALIZE_FAILURE) {
            ErrorToast({ Message: "Failed to load items" });
        }
    }, [cartItemsLoadingStage]);

    const handleSaveItems = async (
        items: ICartItemData[],
        nextStep: string,
        shouldGoToNextStep: boolean,
    ) => {
        const updateItems = transformCartItemsToUpdateCartItemsData(items);
        await dispatch(submitCartDataAndFetch({ items: updateItems, sessionId })).unwrap();
        // Refresh book now items from API
        const updatedItems = await dispatch(fetchBookNowItems(sessionId)).unwrap();
        // Update local state with fetched items
        await dispatch(setBookNowItemData(updatedItems));
        if (shouldGoToNextStep) {
            // After saving items successfully, we know the cart has items with redeemers
            // Pass overrides to avoid stale closure issues where hasRedeemers might be false
            setActiveStep(
                _.findIndex(
                    CheckoutCartStepLabels,
                    (label) => label.trim().toLowerCase() === nextStep,
                ),
                { hasItems: true },
            );
        }
    };

    const proceedWithOrderCreate = async (
        paymentType: string,
        pointsApplied: number,
        commissionApplied?: number | null,
        overrideSessionId?: string,
    ) => {
        const orderSessionId = overrideSessionId || sessionId;
        setIsOrderSubmitting(true);
        try {
            const createOrderResponse = await cartApi.createOrder(
                { paymentType, pointsApplied, commissionApplied },
                orderSessionId,
            );

            const { is_payment_required, bookingReference } = createOrderResponse.data
                .data as IPayNowResponseData & {
                is_payment_required: boolean;
                bookingReference: string;
            };

            if (!is_payment_required) {
                const res = await cartApi.completeOrder(bookingReference);
                if (res?.data?.code === 200) {
                    sessionStorage.removeItem("guestCheckoutInProgress");
                    const guestEmail = sessionStorage.getItem("guestPrimaryEmail");
                    if (guestEmail) {
                        await createGuestAccountPostOrder(guestEmail);
                    }
                    navigate(PATH_BOOKING);
                } else {
                    throw new Error("Failed to complete order");
                }
                return;
            }

            const paymentLink = (
                createOrderResponse.data.data as IPayNowResponseData
            ).payNow.redirectUrl.replaceAll("\\", "");

            ssCheckoutFlow.setItem({ type: "book-now", sessionId: orderSessionId });

            const state = store.getState();
            if (state.auth?.jwt) {
                const tempJwt = ssStorage("tempJwt");
                tempJwt.setItem(state.auth.jwt);
            }

            window.location.href = paymentLink;
        } catch (error: any) {
            console.error("Order creation failed:", error);
            ErrorToast({ Message: error?.response?.data?.title });
        } finally {
            setIsOrderSubmitting(false);
        }
    };

    const handleOrderCreate = async (
        paymentType: string,
        pointsApplied: number,
        commissionApplied?: number | null,
        overrideSessionId?: string,
    ) => {
        const orderArgs = { paymentType, pointsApplied, commissionApplied, overrideSessionId };
        await guestCheckout.ensureGuestAccount(
            () =>
                proceedWithOrderCreate(
                    paymentType,
                    pointsApplied,
                    commissionApplied,
                    overrideSessionId,
                ),
            orderArgs,
            setIsOrderSubmitting,
        );
    };

    const handleHeaderBack = () => {
        if (activeStepIndex > 0) {
            setActiveStep(activeStepIndex - 1);
            return;
        }
        window.history.back();
    };

    const onCreateNewCustomer = (newCustomerData: ISetCustomerData) => {
        return new Promise<any>((resolve, reject) => {
            const createRedeemer = async () => {
                const newCustomer = await dispatch(
                    createNewBookNowRedeemer({ customerData: newCustomerData, sessionId }),
                ).unwrap();
                await dispatch(fetchBookNowRedeemer(sessionId)).unwrap();
                resolve(newCustomer);
            };

            createRedeemer().catch(reject);
        });
    };

    const onOrderCreateSuccess = () => {
        // Don't refetch items to preserve travelers data
        // Items are already saved before order creation
    };

    const showDeletePopUp = (
        items: { id: string | number; type: "cart" | "group" | "product" | "item" }[],
    ) => {
        setDeleteQueue(items);
    };

    const handleDeleteYes = async () => {
        if (!deleteQueue.length) return;
        setIsDeleting(true);
        try {
            await Promise.all(
                deleteQueue.map(({ id }) => cartApi.removeCartItemById(id, sessionId)),
            );
            await dispatch(fetchBookNowItems(sessionId)).unwrap();
        } catch (e) {
            console.error(e);
            ErrorToast({ Message: "Failed to delete items" });
        } finally {
            setIsDeleting(false);
            setDeleteQueue([]);
        }
    };

    const renderComponent = () => {
        const activeStep = BookNowStepLabels[activeStepIndex];
        switch (activeStep) {
            case "Checkout":
                return (
                    <CheckoutStep2
                        items={cartItems}
                        moveTo={setActiveStep}
                        customers={bookNowCustomers}
                        onItemsSave={(items, shouldGoToNextStep) =>
                            handleSaveItems(items, "checkout", shouldGoToNextStep)
                        }
                        onCreateNewCustomer={onCreateNewCustomer}
                        handleOrderCreate={handleOrderCreate}
                        onOrderCreateSuccess={onOrderCreateSuccess}
                    />
                );
            case "Cart":
                return (
                    <CheckoutStep1
                        handleDeleteItems={(ids) =>
                            showDeletePopUp(ids.map((id) => ({ id, type: "item" })))
                        }
                        handleDeleteProduct={(prodId) => {
                            const itemIds = cartItems
                                .filter(
                                    (item) => item.tdms_product_id.toString() === prodId.toString(),
                                )
                                .map((item) => ({ id: item.id, type: "item" as const }));
                            if (itemIds.length > 0) {
                                showDeletePopUp(itemIds);
                            }
                        }}
                        handleDeleteCart={() => showDeletePopUp([{ id: "", type: "cart" }])}
                        onItemsSave={(items, shouldGoToNextStep) =>
                            handleSaveItems(items, "checkout", shouldGoToNextStep)
                        }
                    />
                );
        }
    };

    useEffect(() => {
        // Fetch cart items from API using book now sessionId
        dispatch(fetchBookNowItems(sessionId));
        dispatch(fetchBookNowRedeemer(sessionId));
        setTimeout(() => window.scrollTo(0, 0), 0);
    }, [sessionId, dispatch]);

    return (
        <AppModuleLayout onBack={handleHeaderBack}>
            {/* Wrapped in a fragment: AppModuleLayout types children as
                string | JSX.Element | JSX.Element[], so a bare conditional child
                fails tsc as 'false | Element'. See .agents/memory. */}
            <>
                {shouldPromptForDetails && (
                    <DiscountReadyBanner>
                        <DiscountReadyIcon>&#9733;</DiscountReadyIcon>
                        <div>
                            <DiscountReadyTitle>{checkoutBannerCopy.title}</DiscountReadyTitle>
                            <DiscountReadyBody>{checkoutBannerCopy.body}</DiscountReadyBody>
                        </div>
                    </DiscountReadyBanner>
                )}
                {showDiscountNotice && !shouldPromptForDetails && (
                    <DiscountNotice>
                        <DiscountTick>&#10003;</DiscountTick>
                        <div>
                            <strong>{signupOfferCopy.discountAppliedTitle}</strong>
                            <div>{signupOfferCopy.discountAppliedBody}</div>
                        </div>
                        <DiscountDismiss
                            onClick={() => setShowDiscountNotice(false)}
                            aria-label="Dismiss"
                        >
                            &times;
                        </DiscountDismiss>
                    </DiscountNotice>
                )}
            </>
            <ProcessOrderHeader
                stepLabels={BookNowStepLabels}
                activeStepIndex={activeStepIndex}
                breadcrumbs={BookNowBreadcrumbs}
                changeStep={setActiveStep}
            />
            <Main>
                {cartItemsLoadingStage === LoadingStage.IS_INITIALIZING ? (
                    <Content>
                        <ClipLoaderCommon color={theme.colors.highlight} size={70} margin={150} />
                    </Content>
                ) : cartItems.length === 0 ? (
                    <Container>
                        <Content>
                            <img
                                width={65}
                                height={65}
                                src="/images/cart/emptyCart.svg"
                                alt="emptyCartLogo"
                            />
                            <p>There are no items</p>
                            <NavLink to={PATH_HOME}>
                                <Button>Continue Browsing</Button>
                            </NavLink>
                        </Content>
                    </Container>
                ) : (
                    <CheckoutProvider
                        initialItems={cartItems}
                        checkoutContext={buildBookNowCheckoutContext()}
                        activeDiscount={activeDiscount?.discount}
                        sessionId={sessionId}
                    >
                        {renderComponent()}
                    </CheckoutProvider>
                )}
                <ConfirmComponent
                    itemIds={deleteQueue}
                    isDeleting={isDeleting}
                    onClickOverlay={() => setDeleteQueue([])}
                    onClickBtnNo={() => setDeleteQueue([])}
                    onClickBtnYes={handleDeleteYes}
                >
                    {isDeleting
                        ? "Deleting items from cart"
                        : `Are you sure you want to delete ${
                              deleteQueue.length > 1 ? "these items" : "this item"
                          }?`}
                </ConfirmComponent>
                {guestCheckout.showGuestLoginModal && (
                    <GuestLoginModal
                        email={guestCheckout.guestLoginEmail}
                        password={guestCheckout.guestLoginPassword}
                        onPasswordChange={guestCheckout.setGuestLoginPassword}
                        showPassword={guestCheckout.showGuestLoginPassword}
                        onToggleShowPassword={() =>
                            guestCheckout.setShowGuestLoginPassword(
                                !guestCheckout.showGuestLoginPassword,
                            )
                        }
                        isLoggingIn={guestCheckout.isGuestLoggingIn}
                        onLogin={() =>
                            guestCheckout.handleGuestLogin(async () => {
                                if (guestCheckout.pendingOrderArgs) {
                                    const args = guestCheckout.pendingOrderArgs;
                                    await proceedWithOrderCreate(
                                        args.paymentType,
                                        args.pointsApplied,
                                        args.commissionApplied,
                                        args.overrideSessionId,
                                    );
                                }
                            })
                        }
                        onClose={guestCheckout.closeGuestLoginModal}
                        onForgotPassword={() => window.open("/forgot-password", "_blank")}
                    />
                )}
                {isOrderSubmitting && <PaymentRedirectOverlay />}
            </Main>
        </AppModuleLayout>
    );
};

const Main = styled.div`
    flex: 1;
    align-items: center;
    background-color: #f2f2f2;

    ${breakpoint.breakTablet`
        padding-bottom: 30px;
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
    `}
`;

const Container = styled.div`
    background-color: white;
    display: flex;
    flex: 1;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    padding: 30px;
`;

const Content = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 14px;
`;

const Button = styled.button`
    border: 1px solid ${theme.colors.main};
    padding: 12px 24px;
    border-radius: 4px;
    color: ${theme.colors.button_text1};
    background: none;
    cursor: pointer;
`;

const DiscountNotice = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin: 12px 16px 0;
    padding: 12px 14px;
    border-radius: ${theme.borderRadius.small};
    background: #e8f7ed;
    border: 1px solid #b7e4c7;
    font-size: 14px;
    line-height: 1.4;
    color: ${theme.colors.black};
`;

const DiscountTick = styled.span`
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #2e9e5b;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
`;

const DiscountDismiss = styled.button`
    margin-left: auto;
    border: none;
    background: none;
    font-size: 20px;
    line-height: 1;
    color: ${theme.colors.gray_3};
    cursor: pointer;
`;

const DiscountReadyBanner = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    box-sizing: border-box;
    /* Matches the width steps the checkout content uses (CheckoutStep2), so the
       banner lines up with the cards beneath it rather than spanning the page. */
    width: calc(100% - 24px);
    margin: 16px auto 8px;
    padding: 16px 20px;
    border-radius: ${theme.borderRadius.small};
    background: linear-gradient(90deg, #fff3cd 0%, #ffe28a 100%);
    border: 1px solid ${theme.colors.main};
    border-bottom: 3px solid ${theme.colors.main};
    color: ${theme.colors.black};

    ${breakpoint.breakTablet`
        width: ${(p) => p.theme.widths.wide_700};
    `}

    ${breakpoint.breakIpadPro`
        width: ${(p) => p.theme.widths.wide_900};
    `}

    ${breakpoint.breakLarge`
        width: ${(p) => p.theme.widths.wide_1110};
    `}

    ${breakpoint.breakOnlyMobile`
        padding: 14px 16px;
    `}
`;

const DiscountReadyIcon = styled.span`
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: ${theme.colors.main};
    color: ${theme.colors.black};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
`;

const DiscountReadyTitle = styled.div`
    font-size: 17px;
    font-weight: 700;
    line-height: 1.2;
`;

const DiscountReadyBody = styled.div`
    font-size: 14px;
    line-height: 1.4;
    color: ${theme.colors.gray_1};
`;
